import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { throwUserFacingHttpError, toUserFacingApiError } from "lib/utils/userFacingApiError";

/** HTTP API for mobile broadband on the robot (FastAPI + mmcli), default port 12403. */

export type MobileStatusResponse = {
    modem_index: number;
    present: boolean;
    manufacturer?: string | null;
    model?: string | null;
    state?: string | null;
    power_state?: string | null;
    access_tech?: string | null;
    signal_quality_percent?: number | null;
    operator_name?: string | null;
    operator_id?: string | null;
    registration?: string | null;
    packet_service_state?: string | null;
    imei?: string | null;
    apn?: string | null;
    /** modem | android_db — where apn was resolved from */
    apn_source?: string | null;
    apn_suggested?: string | null;
    apn_carrier?: string | null;
    apn_username?: string | null;
    bearer_paths?: string[];
    bearer_connected?: boolean | null;
    ecm_iface?: string | null;
    ecm_ipv4?: string | null;
    data_path?: "ecm" | "mmcli" | null;
    internet_connected?: boolean | null;
    internet_connectivity_checked_at?: string | null;
    internet_connectivity_iface?: string | null;
    internet_connectivity_ipv4?: string | null;
    internet_reachable_targets?: string[];
    /** Persisted On/Off preference; null/undefined when never set by the user. */
    desired_mobile_data_enabled?: boolean | null;
    error?: string | null;
};

export type MobilePowerRequest = {
    enabled: boolean;
    reconnect?: boolean;
};

export type MobileReconnectRequest = {
    apn?: string | null;
    username?: string | null;
    password?: string | null;
};

export type MobileApnRequest = {
    /** Omit or empty to apply the Android-catalog suggestion for the SIM. */
    apn?: string | null;
    username?: string | null;
    password?: string | null;
    reconnect?: boolean;
};

export type MobileActionResponse = {
    ok: boolean;
    message: string;
};

const DEFAULT_MOBILE_NETWORK_API_PORT = "12403";

/** Abort hanging GET /mobile/status when ModemManager is wedged. */
export const MOBILE_STATUS_TIMEOUT_MS = 15_000;
/** Power / APN / reconnect can run long mmcli + DHCP sequences. */
export const MOBILE_ACTION_TIMEOUT_MS = 90_000;

export class MobileNetworkTimeoutError extends Error {
    constructor(message = "Mobile network request timed out") {
        super(message);
        this.name = "MobileNetworkTimeoutError";
    }
}


async function authedFetchWithTimeout(
    url: string,
    init: RequestInit | undefined,
    timeoutMs: number
): Promise<Response> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await authedFetch(url, { ...init, signal: controller.signal });
    } catch (e) {
        if (
            (e instanceof DOMException || e instanceof Error) &&
            e.name === "AbortError"
        ) {
            throw new MobileNetworkTimeoutError();
        }
        throw e;
    } finally {
        window.clearTimeout(timer);
    }
}

/** Base URL with no trailing slash. Host resolution matches Ethernet / Wi-Fi pattern. */
export function getMobileNetworkApiBaseUrl(selectedArcHost?: string | null, isSecure: boolean = isHttpSecure(), arcId?: string | null): string | null {
    if (process.env.REACT_APP_MOBILE_NETWORK_API_DISABLED === "true") {
        return null;
    }
    const explicit = process.env.REACT_APP_MOBILE_NETWORK_API_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port =
        process.env.REACT_APP_MOBILE_NETWORK_API_PORT?.trim() || DEFAULT_MOBILE_NETWORK_API_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_MOBILE_NETWORK_API_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("mobile", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

export async function fetchMobileStatus(baseUrl: string): Promise<MobileStatusResponse> {
    const url = joinUrl(baseUrl, "/mobile/status");
    const res = await authedFetchWithTimeout(url, undefined, MOBILE_STATUS_TIMEOUT_MS);
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't load mobile network status. Try again.");
    const data = (await res.json()) as MobileStatusResponse;
    if (data.error) {
        data.error = toUserFacingApiError(
            data.error,
            data.present === false
                ? "No mobile modem detected."
                : "Couldn't load mobile network status. Try again."
        );
    }
    if (data.present === false && !data.error) {
        data.error = "No mobile modem detected.";
    }
    return data;
}

export async function postMobilePower(
    baseUrl: string,
    body: MobilePowerRequest
): Promise<MobileActionResponse> {
    const url = joinUrl(baseUrl, "/mobile/power");
    const res = await authedFetchWithTimeout(
        url,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ enabled: body.enabled, reconnect: body.reconnect !== false }),
        },
        MOBILE_ACTION_TIMEOUT_MS
    );
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't change mobile data. Try again.");
    return res.json() as Promise<MobileActionResponse>;
}

export async function postMobileApn(
    baseUrl: string,
    body: MobileApnRequest
): Promise<MobileActionResponse> {
    const url = joinUrl(baseUrl, "/mobile/apn");
    const res = await authedFetchWithTimeout(
        url,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                apn: body.apn?.trim() || null,
                username: body.username?.trim() || null,
                password: body.password || null,
                reconnect: body.reconnect !== false,
            }),
        },
        MOBILE_ACTION_TIMEOUT_MS
    );
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't update the APN. Try again.");
    return res.json() as Promise<MobileActionResponse>;
}

export async function postMobileReconnect(
    baseUrl: string,
    body: MobileReconnectRequest = {}
): Promise<MobileActionResponse> {
    const url = joinUrl(baseUrl, "/mobile/reconnect");
    const res = await authedFetchWithTimeout(
        url,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                apn: body.apn?.trim() || null,
                username: body.username?.trim() || null,
                password: body.password || null,
            }),
        },
        MOBILE_ACTION_TIMEOUT_MS
    );
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't reconnect mobile data. Try again.");
    return res.json() as Promise<MobileActionResponse>;
}
